import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import axios from 'axios';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Lessons = () => {
  const { token } = useAuth();
  const { audience } = usePreferences();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('beginner');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLessons();
  }, [selectedLevel, audience]);

  const fetchLessons = async () => {
    try {
      const response = await axios.get(`${API}/lessons`, {
        params: { level: selectedLevel, audience }
      });
      setLessons(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des leçons');
    } finally {
      setLoading(false);
    }
  };

  const levels = [
    { value: 'beginner', label: 'Débutant', color: 'bg-accent' },
    { value: 'intermediate', label: 'Intermédiaire', color: 'bg-secondary' },
    { value: 'advanced', label: 'Avancé', color: 'bg-primary' }
  ];

  return (
    <div className="min-h-screen bg-sand">
      <nav className="bg-white border-b-2 border-stone-200 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="tile-button bg-stone-200 text-charcoal p-2 rounded-lg hover:bg-stone-300"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-playfair text-3xl font-bold text-primary" data-testid="lessons-title">
            Leçons
          </h1>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <div className="flex flex-wrap gap-3 mb-8">
          {levels.map((level) => (
            <button
              key={level.value}
              onClick={() => setSelectedLevel(level.value)}
              data-testid={`level-${level.value}`}
              className={`tile-button font-nunito font-bold py-2 px-6 rounded-lg ${
                selectedLevel === level.value
                  ? `${level.color} text-white shadow-[0_3px_0_rgba(0,0,0,0.3)]`
                  : 'bg-white text-charcoal border-2 border-stone-200 hover:border-primary'
              }`}
            >
              {level.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="font-nunito text-xl text-charcoal">Chargement...</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {lessons.map((lesson, index) => (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                data-testid={`lesson-card-${index}`}
                className="bg-white border-2 border-stone-200 rounded-xl p-6 shadow-sm hover:shadow-lg card-hover cursor-pointer"
                onClick={() => navigate(`/lesson/${lesson.id}`)}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <BookOpen className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-nunito font-bold text-white ${
                      lesson.level === 'beginner' ? 'bg-accent' : lesson.level === 'intermediate' ? 'bg-secondary' : 'bg-primary'
                    }`}>
                      {lesson.level === 'beginner' ? 'Débutant' : lesson.level === 'intermediate' ? 'Intermédiaire' : 'Avancé'}
                    </span>
                  </div>
                </div>
                
                <h3 className="font-playfair text-xl font-bold text-charcoal mb-2">
                  {lesson.title}
                </h3>
                <p className="font-nunito text-charcoal/70">
                  {lesson.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {!loading && lessons.length === 0 && (
          <div className="text-center py-12">
            <p className="font-nunito text-xl text-charcoal/60">Aucune leçon disponible pour ce niveau</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lessons;